import React, { useCallback, useContext, useMemo, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import { useLazyQuery, useMutation } from '@apollo/react-hooks'
import {
  Col,
  Collapse,
  Form as AntForm,
  Icon,
  List,
  Row,
  Typography,
  Upload,
} from 'antd'
import fileDownload from 'js-file-download'
import { isEmpty, isEqual, isNull, map, pick, size } from 'lodash-es'
import PropTypes from 'prop-types'

import {
  Breadcrumb,
  FormDate,
  FormInternalLink,
  FormLink,
  FormText,
  HiddenCheckboxes,
  HiddenInputs,
} from '../../../core/components'
import { Button } from '../../../core/components/styled'
import { THEME } from '../../../core/constants'
import { getQueryParams } from '../../../core/utils/services/queryParams'

import {
  DocumentMenuScrapingHistoryDrawer,
  HistoryDrawer,
  Spinner,
  VisibilityContainer,
} from '../../../common/components'
import {
  PageActions,
  PageHeader,
  PageTitle,
} from '../../../common/components/styled'
import {
  DEFAULT_PAGE,
  DISH_REVIEW_TASK,
  DISH_REVIEW_TASKS_PERMISSIONS,
  EMPTY_VALUE,
  MENU_SCRAPING_JOB_STATUSES,
  SCRAPING_ENGINES,
} from '../../../common/constants'
import {
  showErrorNotification,
  showSuccessNotification,
} from '../../../common/helpers'

import { AuthContext } from '../../../account/contexts'
import { DocumentMenuScrapingDebugModal } from '../../../menus/components/pages'
import {
  DocumentMenuTitlesAndDishesTables,
  ScrapingAnalysis,
  ScrapingAnalysisAi,
} from '../../../menus/components/pages/sections/document-menus'
import {
  AUTO_REJECTED,
  IMAGE,
  MENU_FORMATS,
  PDF,
  REJECTED,
} from '../../../menus/constants'
import { MENU_DOCUMENT_FILE_URL } from '../../../menus/graphql/queries'
import { isMenuScrapeable } from '../../../menus/helpers'
import {
  DISH_REVIEW,
  MANUAL_CHANGE,
  REOPENED,
  REQUIRED_DISH_FIELDS,
  REQUIRED_MENU_TITLE_FIELDS,
  RESOLVED,
  TASK_AWAITING_REASONS,
  TASK_REOPEN_REASONS,
} from '../../constants'
import {
  ASSIGN_DISH_REVIEW_TASK,
  MARK_DISH_REVIEW_TASK_AS_AWAITING,
  MARK_DISH_REVIEW_TASK_MISC_AS_QC_VERIFIED,
  MARK_DISH_REVIEW_TASK_MISC_AS_VERIFIED,
  REOPEN_DISH_REVIEW_TASK,
  UNASSIGN_DISH_REVIEW_TASK,
  UNMARK_DISH_REVIEW_TASK_AS_AWAITING,
} from '../../graphql/mutations'
import {
  getBreadcrumbItemsForDishReviewTaskPage,
  processUploadedMenuData,
  validateUploadedMenuData,
} from '../../helpers'
import {
  AwaitingReasonField,
  LocationsList,
  MarkMiscTaskAsVerifiedModal,
  MenuAvailabilityCommentModal,
  MiscTaskVerificationThreeStateSwitch,
  RejectAiMenuScrapingModal,
  TaskActionSection,
} from '../pages/sections'

import { dishReviewTaskFormInitialValues } from './initial-values'

const DocumentDishReviewTaskForm = ({
  task,
  isSavingTask,
  isResolvingTask,
  onSubmit,
}) => {
  const { user: loggedInUser } = useContext(AuthContext)

  const [
    isMenuAvailabilityCommentModalOpen,
    setIsMenuAvailabilityCommentModalOpen,
  ] = useState(false)

  const [isRejectMenuModalOpen, setIsRejectMenuModalOpen] = useState(false)

  const [isScrapingDebugModalOpen, setIsScrapingDebugModalOpen] = useState(
    false,
  )

  const [selectedItemHash, setSelectedItemHash] = useState(null)

  const [isQCVerified, setIsQCVerified] = useState(false)

  const [
    isMarkMiscTaskAsVerifiedModalOpen,
    setIsMarkMiscTaskAsVerifiedModalOpen,
  ] = useState(false)

  const [uploadedMenuData, setUploadedMenuData] = useState(null)

  const { search } = useLocation()
  const { page = DEFAULT_PAGE, pageSize = 40 } = getQueryParams(search)

  const formInitialValues = useMemo(
    () => dishReviewTaskFormInitialValues(task),
    [task],
  )

  const scrapedDocumentFile = useMemo(() => {
    const scrapingCommand = task.menu.documentMenuScrapingJob?.scrapingCommand
      ? JSON.parse(task.menu.documentMenuScrapingJob.scrapingCommand)
      : {}

    return {
      fileKey: scrapingCommand.fileKey,
      fileBucketName: scrapingCommand.bucketName,
      fileName: scrapingCommand.fileName,
    }
  }, [task.menu.documentMenuScrapingJob])

  const formMethods = useForm({
    defaultValues: formInitialValues,
  })
  const { handleSubmit, getValues, setValue, control, watch } = formMethods

  const isScrapeable = isMenuScrapeable(watch('menu.format'))

  const menuTitlesFieldArray = useFieldArray({
    control,
    name: 'menuTitles',
  })
  const dishesFieldArray = useFieldArray({
    control,
    name: 'dishes',
  })

  const {
    fields: menuTitlesFields,
    remove: removeMenuTitles,
    append: appendMenuTitles,
  } = menuTitlesFieldArray
  const {
    fields: dishesFields,
    remove: removeDishes,
    append: appendDishes,
  } = dishesFieldArray

  const { menu } = task
  const taskStatus = watch('task.status')
  const miscTaskVerifiedComment = watch('task.miscTaskVerifiedComment')
  const miscTaskQCVerifiedComment = watch('task.miscTaskQCVerifiedComment')

  const pageTitle = useMemo(
    () =>
      menu.format === PDF || menu.format === IMAGE
        ? 'Document Dish Review Task'
        : 'HTML Dish Review Task',
    [menu.format],
  )

  const [assignTask, { loading: isAssigningTask }] = useMutation(
    ASSIGN_DISH_REVIEW_TASK,
    {
      onCompleted({ assignDishReviewTask }) {
        const { task } = getValues()

        const updatedTask = {
          ...task,
          modifiedDate: assignDishReviewTask.modifiedDate,
          isAssigned: true,
          assignedTo: {
            id: loggedInUser.id,
            name: loggedInUser.name,
          },
        }

        setValue('task', updatedTask)

        showSuccessNotification({
          message: 'Dish review task assigned.',
        })
      },
      onError({ message }) {
        showErrorNotification({
          message: 'Dish review task assign failed.',
          description: message,
        })
      },
    },
  )

  const [unassignTask, { loading: isUnassigningTask }] = useMutation(
    UNASSIGN_DISH_REVIEW_TASK,
    {
      onCompleted({ unassignDishReviewTask }) {
        const { task } = getValues()

        const updatedTask = {
          ...task,
          modifiedDate: unassignDishReviewTask.modifiedDate,
          isAssigned: false,
          assignedTo: {
            id: '',
            name: '',
          },
        }

        setValue('task', updatedTask)

        showSuccessNotification({
          message: 'Dish review task unassigned.',
        })
      },
      onError({ message }) {
        showErrorNotification({
          message: 'Dish review task unassign failed.',
          description: message,
        })
      },
    },
  )

  const [
    markTaskAsAwaiting,
    { loading: isMarkingTaskAsAwaiting },
  ] = useMutation(MARK_DISH_REVIEW_TASK_AS_AWAITING, {
    onCompleted({ markDishReviewTaskAsAwaiting }) {
      const { task } = getValues()

      const updatedTask = {
        ...task,
        isAwaiting: true,
        modifiedDate: markDishReviewTaskAsAwaiting.modifiedDate,
        awaitingReason:
          TASK_AWAITING_REASONS[markDishReviewTaskAsAwaiting.awaitingReason]
            .name,
        awaitingReasonComment:
          markDishReviewTaskAsAwaiting.awaitingReasonComment,
      }

      setValue('task', updatedTask)

      showSuccessNotification({
        message: 'Dish review task marked as awaiting.',
      })
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Dish review task mark as awaiting failed.',
        description: message,
      })
    },
  })

  const [
    unmarkTaskAsAwaiting,
    { loading: isUnmarkingTaskAsAwaiting },
  ] = useMutation(UNMARK_DISH_REVIEW_TASK_AS_AWAITING, {
    onCompleted({ unmarkDishReviewTaskAsAwaiting }) {
      const { task } = getValues()

      const updatedTask = {
        ...task,
        modifiedDate: unmarkDishReviewTaskAsAwaiting.modifiedDate,
        isAwaiting: false,
        awaitingReason: '',
        awaitingReasonComment: '',
      }

      setValue('task', updatedTask)

      showSuccessNotification({
        message: 'Dish review task unmarked as awaiting.',
      })
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Dish review task unmark as awaiting failed.',
        description: message,
      })
    },
  })

  const [
    markMiscTaskAsVerified,
    { loading: isMarkingMiscTaskAsVerified },
  ] = useMutation(MARK_DISH_REVIEW_TASK_MISC_AS_VERIFIED, {
    onCompleted({
      markDishReviewTaskMiscAsVerified: {
        miscTaskVerifiedDate,
        miscTaskVerifiedComment,
      },
    }) {
      const { task } = getValues()

      const updatedTask = {
        ...task,
        miscTaskVerifiedBy: {
          id: loggedInUser.id,
          name: loggedInUser.name,
        },
        miscTaskVerifiedDate,
        miscTaskVerifiedComment,
      }

      setValue('task', updatedTask)

      showSuccessNotification({
        message: 'Miscellaneous task marked as verified.',
      })
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Miscellaneous task mark as verified failed.',
        description: message,
      })
    },
  })

  const [
    markMiscTaskAsQCVerified,
    { loading: isMarkingMiscTaskAsQCVerified },
  ] = useMutation(MARK_DISH_REVIEW_TASK_MISC_AS_QC_VERIFIED, {
    onCompleted({
      markDishReviewTaskMiscAsQCVerified: {
        miscTaskQCVerifiedDate,
        miscTaskQCVerifiedComment,
      },
    }) {
      const { task } = getValues()

      const updatedTask = {
        ...task,
        miscTaskQCVerifiedBy: {
          id: loggedInUser.id,
          name: loggedInUser.name,
        },
        miscTaskQCVerifiedDate,
        miscTaskQCVerifiedComment,
      }

      setValue('task', updatedTask)

      showSuccessNotification({
        message: 'Miscellaneous task marked as QC verified.',
      })
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Miscellaneous task mark as QC verified failed.',
        description: message,
      })
    },
  })

  const [reopenTask, { loading: isReopeningTask }] = useMutation(
    REOPEN_DISH_REVIEW_TASK,
    {
      onCompleted({ reopenDishReviewTask: { modifiedDate, reopenedDate } }) {
        const { task } = getValues()

        const updatedTask = {
          ...task,
          modifiedDate,
          reopenedBy: {
            id: loggedInUser.id,
            name: loggedInUser.name,
          },
          reopenedDate,
          reopenReason: TASK_REOPEN_REASONS[MANUAL_CHANGE].name,
          status: REOPENED,
        }

        setValue('task', updatedTask)

        showSuccessNotification({
          message: 'Dish review task reopened.',
        })
      },
      onError({ message }) {
        showErrorNotification({
          message: 'Dish review task reopen failed.',
          description: message,
        })
      },
    },
  )

  const [
    getMenuDocumentFile,
    { data: { menuDocumentFileUrl } = {} },
  ] = useLazyQuery(MENU_DOCUMENT_FILE_URL, {
    fetchPolicy: 'no-cache',
    onCompleted() {
      window.open(menuDocumentFileUrl, '_blank')
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Download menu document file failed.',
        description: message,
      })
    },
  })

  const onAssignTask = useCallback(
    () =>
      assignTask({
        variables: { id: task.id, toUserId: loggedInUser.id },
      }),
    [assignTask, task, loggedInUser],
  )

  const onUnassignTask = useCallback(
    () => unassignTask({ variables: { id: task.id } }),
    [unassignTask, task],
  )

  const onSave = useCallback(async () => {
    setValue('task.isDone', false)

    if (uploadedMenuData) {
      setValue('uploadedMenuData', {
        menuTitles: uploadedMenuData.menuTitles,
        dishes: uploadedMenuData.dishes,
      })
    }

    handleSubmit(onSubmit)()
  }, [setValue, handleSubmit, onSubmit, uploadedMenuData])

  const onDone = useCallback(async () => {
    setValue('task.isDone', true)

    if (uploadedMenuData) {
      setValue('uploadedMenuData', {
        menuTitles: uploadedMenuData.menuTitles,
        dishes: uploadedMenuData.dishes,
      })
    }

    handleSubmit(onSubmit)()
  }, [setValue, handleSubmit, onSubmit, uploadedMenuData])

  const onMarkTaskAsAwaiting = useCallback(
    awaitingReasonData =>
      markTaskAsAwaiting({
        variables: { id: task.id, ...awaitingReasonData },
      }),
    [markTaskAsAwaiting, task],
  )

  const onUnmarkTaskAsAwaiting = useCallback(() => {
    unmarkTaskAsAwaiting({ variables: { id: task.id } })
  }, [unmarkTaskAsAwaiting, task])

  const onReopenTask = useCallback(
    () => reopenTask({ variables: { id: task.id } }),
    [reopenTask, task],
  )

  const handleOpenScrapingDebugModal = useCallback(hash => {
    setSelectedItemHash(hash)
    setIsScrapingDebugModalOpen(true)
  }, [])

  const handleCloseScrapingDebugModal = useCallback(() => {
    setSelectedItemHash(null)
    setIsScrapingDebugModalOpen(false)
  }, [])

  const handleFileUpload = useCallback(
    file => {
      const reader = new FileReader()
      reader.onload = e => {
        try {
          const uploadedJsonData = JSON.parse(e.target.result)

          const validation = validateUploadedMenuData(uploadedJsonData)
          if (!validation.isValid) {
            showErrorNotification({
              message: validation.error,
            })
            return
          }

          const jsonData = processUploadedMenuData(uploadedJsonData)

          const currentMenuTitles = menuTitlesFields
          const currentDishes = dishesFields

          const isDataDifferent =
            !isEqual(currentMenuTitles, jsonData.menuTitles) ||
            !isEqual(currentDishes, jsonData.dishes)

          if (!isDataDifferent) {
            showErrorNotification({
              message: 'Uploaded data is identical to current data',
            })
            return
          }

          setUploadedMenuData(jsonData)

          setValue('menu.dishCount', size(jsonData.dishes))

          removeMenuTitles()
          map(jsonData.menuTitles, menuTitle => appendMenuTitles(menuTitle))

          removeDishes()
          map(jsonData.dishes, dish => appendDishes(dish))

          showSuccessNotification({
            message: 'Menu data uploaded successfully',
          })
        } catch (error) {
          showErrorNotification({
            message: 'Invalid JSON file',
          })
        }
      }
      reader.readAsText(file)
    },
    [
      menuTitlesFields,
      dishesFields,
      removeMenuTitles,
      appendMenuTitles,
      removeDishes,
      appendDishes,
      setUploadedMenuData,
      setValue,
    ],
  )

  const handleDownloadMenuData = useCallback(() => {
    try {
      const currentMenuTitles = menuTitlesFields
      const currentDishes = dishesFields

      if (isEmpty(currentMenuTitles) || isEmpty(currentDishes)) {
        showErrorNotification({
          message: 'No menu data available for download',
        })
        return
      }

      const filteredMenuTitles = map(currentMenuTitles, menuTitle =>
        pick(menuTitle, REQUIRED_MENU_TITLE_FIELDS),
      )

      const filteredDishes = map(currentDishes, dish =>
        pick(dish, REQUIRED_DISH_FIELDS),
      )

      const menuData = {
        menuTitles: filteredMenuTitles,
        dishes: filteredDishes,
      }

      const dataStr = JSON.stringify(menuData, null, 2)
      fileDownload(
        dataStr,
        `menu-data-${task.menu.id}-${
          new Date().toISOString().split('T')[0]
        }.json`,
      )

      showSuccessNotification({
        message: 'Menu data downloaded successfully',
      })
    } catch (error) {
      showErrorNotification({
        message: 'Failed to download menu data',
      })
    }
  }, [menuTitlesFields, dishesFields, task.menu.id])

  const isRejectButtonDisabled = useMemo(
    () =>
      taskStatus === RESOLVED ||
      menu.status === AUTO_REJECTED ||
      menu.status === REJECTED,
    [menu.status, taskStatus],
  )

  const isScrapingDetailsButtonDisabled = useMemo(
    () =>
      menu.status === AUTO_REJECTED ||
      menu.documentMenuScrapingJob?.scrapingEngine ===
        SCRAPING_ENGINES.MULTI_FORMAT,
    [menu],
  )

  const breadcrumbItems = useMemo(
    () => getBreadcrumbItemsForDishReviewTaskPage(task),
    [task],
  )

  const onVerifiedButtonClick = () => {
    setIsQCVerified(false)
    setIsMarkMiscTaskAsVerifiedModalOpen(true)
  }

  const onQCVerifiedButtonClick = () => {
    setIsQCVerified(true)
    setIsMarkMiscTaskAsVerifiedModalOpen(true)
  }

  const handleMarkMiscTaskAsVerified = useCallback(
    ({ comment }) => {
      const data = { variables: { id: task.id, comment } }
      setIsMarkMiscTaskAsVerifiedModalOpen(false)

      return isQCVerified
        ? markMiscTaskAsQCVerified(data)
        : markMiscTaskAsVerified(data)
    },
    [isQCVerified, markMiscTaskAsVerified, markMiscTaskAsQCVerified, task],
  )

  const onDocumentFileNameClick = useCallback(() => {
    const { fileKey, fileBucketName } = scrapedDocumentFile

    getMenuDocumentFile({
      variables: {
        fileKey,
        fileBucketName,
      },
    })
  }, [scrapedDocumentFile, getMenuDocumentFile])

  return (
    <>
      {(isAssigningTask ||
        isUnassigningTask ||
        isSavingTask ||
        isResolvingTask ||
        isMarkingTaskAsAwaiting ||
        isUnmarkingTaskAsAwaiting ||
        isReopeningTask) && <Spinner size="large" />}

      <Breadcrumb currentTaskType={DISH_REVIEW} items={breadcrumbItems} />

      <PageHeader sticky>
        <PageTitle>
          {pageTitle}
          <HistoryDrawer entityType={DISH_REVIEW_TASK} entityId={task.id} />
        </PageTitle>
        <PageActions>
          <TaskActionSection
            task={watch('task')}
            taskType={DISH_REVIEW}
            permissions={DISH_REVIEW_TASKS_PERMISSIONS}
            onAssignTask={onAssignTask}
            onUnassignTask={onUnassignTask}
            onSave={onSave}
            onDone={onDone}
            onMarkTaskAsAwaiting={onMarkTaskAsAwaiting}
            onUnmarkTaskAsAwaiting={onUnmarkTaskAsAwaiting}
            onReopenTask={onReopenTask}
            cancelUrl="/tasks/dish-review"
            menu={watch('menu')}
          />
        </PageActions>
      </PageHeader>

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Collapse
            defaultActiveKey={[
              'menu-section',
              'task-section',
              'scraping-summary-section',
            ]}
          >
            <Collapse.Panel header="Menu" key="menu-section">
              <Row gutter={[16, 16]}>
                <Col span={2}>
                  <FormInternalLink
                    transform={() => `/menus/${task.menu.id}`}
                    name="menu.id"
                    label="ID"
                  />
                </Col>
                <Col span={5}>
                  <FormLink name="menu.url" label="URL" />
                </Col>
                <Col span={2}>
                  <FormText
                    name="menu.format"
                    label="Format"
                    transform={value => MENU_FORMATS[value]?.name || value}
                  />
                </Col>
                <Col span={2}>
                  <FormText name="menu.dishCount" label="Dishes #" />
                </Col>
                <Col span={4}>
                  <FormText
                    copyable={{ text: task.menu.brand.name }}
                    name="menu.brand.name"
                    label="Brand"
                  />
                </Col>
                {!isEmpty(task.menu.locations) && (
                  <Col span={4}>
                    <LocationsList name="menu.locations" label="Location(s):" />
                  </Col>
                )}
                <Col span={2}>
                  <AntForm.Item label="Availability">
                    <Button
                      disabled={isNull(menu.availabilityComment)}
                      onClick={() =>
                        setIsMenuAvailabilityCommentModalOpen(true)
                      }
                    >
                      <Icon type="audit" />
                    </Button>
                  </AntForm.Item>
                </Col>
                <Col span={3}>
                  <FormText name="menu.status" label="Status" />
                </Col>
              </Row>
              <Row gutter={[24, 16]}>
                {isScrapeable && (
                  <>
                    <Col span={3}>
                      <AntForm.Item label="Scraping history">
                        <DocumentMenuScrapingHistoryDrawer
                          menuId={task.menu.id}
                          buttonName="View"
                          buttonSize="default"
                        />
                      </AntForm.Item>
                    </Col>
                  </>
                )}
                <Col span={3}>
                  <FormDate
                    name="menu.scrapingRejectedDate"
                    label="Scraping rejected at"
                  />
                </Col>
                <Col span={3}>
                  <FormText
                    name="menu.scrapingRejectedBy.name"
                    label="Scraping rejected by"
                  />
                </Col>
                <VisibilityContainer
                  isHidden={task.menu.status !== AUTO_REJECTED}
                >
                  <Col span={10}>
                    <FormText
                      name="menu.scrapingRejectedReason"
                      label="Scraping rejected reason"
                    />
                  </Col>
                </VisibilityContainer>
                <VisibilityContainer
                  isHidden={task.menu.status === AUTO_REJECTED}
                >
                  <Col span={8}>
                    <FormText
                      name="menu.scrapingRejectedComment"
                      label="Scraping rejected comment"
                      ellipsis={false}
                      style={{ whiteSpace: 'break-spaces' }}
                    />
                  </Col>
                </VisibilityContainer>
                <Col span={7}>
                  <AntForm.Item label="Actions">
                    <Button
                      margin="no small small no"
                      disabled={isRejectButtonDisabled}
                      onClick={() => setIsRejectMenuModalOpen(true)}
                    >
                      Reject
                      <Icon type="stop" />
                    </Button>

                    <Button
                      margin="no small small no"
                      onClick={handleDownloadMenuData}
                    >
                      Download
                      <Icon type="download" />
                    </Button>

                    <Upload
                      accept=".json"
                      showUploadList={false}
                      beforeUpload={file => {
                        handleFileUpload(file)
                        return false
                      }}
                    >
                      <Button margin="no small no no">
                        Upload
                        <Icon type="upload" />
                      </Button>
                    </Upload>
                  </AntForm.Item>
                </Col>
              </Row>
            </Collapse.Panel>
            <Collapse.Panel header="Task" key="task-section">
              <Row gutter={16}>
                <Col span={2}>
                  <FormText
                    name="task.isAutoResolved"
                    label="Is auto resolved"
                  />
                </Col>
                <Col span={3}>
                  <AwaitingReasonField
                    valueFieldName="task.awaitingReason"
                    titleFieldName="task.awaitingReasonComment"
                    label="Awaiting reason"
                  />
                </Col>
                <Col span={3}>
                  <FormDate label="Updated at" name="task.modifiedDate" />
                </Col>
                <VisibilityContainer isHidden={taskStatus !== REOPENED}>
                  <>
                    <Col span={2}>
                      <FormText
                        label="Reopened by"
                        name="task.reopenedBy.name"
                      />
                    </Col>
                    <Col span={3}>
                      <FormDate label="Reopened at" name="task.reopenedDate" />
                    </Col>
                    <Col span={2}>
                      <FormText
                        label="Reopen reason"
                        name="task.reopenReason"
                      />
                    </Col>
                  </>
                </VisibilityContainer>
                <Col span={2}>
                  <FormText
                    name="task.isRerunFlow"
                    label="Rerun flow"
                    style={{
                      textAlign: task.isRerunFlow ? 'center' : '',
                      backgroundColor: task.isRerunFlow
                        ? THEME.colors.orange
                        : '',
                    }}
                  />
                </Col>
                <Col span={3}>
                  <AntForm.Item label="Miscellaneous task">
                    <MiscTaskVerificationThreeStateSwitch
                      task={watch('task')}
                      onVerifiedButtonClick={onVerifiedButtonClick}
                      onQCVerifiedButtonClick={onQCVerifiedButtonClick}
                      loading={
                        isMarkingMiscTaskAsVerified ||
                        isMarkingMiscTaskAsQCVerified
                      }
                      loggedInUser={loggedInUser}
                    />
                  </AntForm.Item>
                </Col>
              </Row>
            </Collapse.Panel>
            <Collapse.Panel
              header="Scraping Summary"
              key="scraping-summary-section"
            >
              <Row gutter={16}>
                <Col span={3}>
                  <FormDate
                    label="Date"
                    name="menu.documentMenuScrapingJob.completedDate"
                  />
                </Col>
                <Col span={3}>
                  <FormText
                    label="Duration"
                    name="menu.documentMenuScrapingJob.scrapingDuration"
                    valuePostfix="s"
                  />
                </Col>
                <Col span={3}>
                  <FormText
                    label="Status"
                    name="menu.documentMenuScrapingJob.scrapingStatus"
                  />
                </Col>
                <Col span={3}>
                  <FormText
                    label="Reason"
                    name="menu.documentMenuScrapingJob.scrapingReason"
                  />
                </Col>
                <Col span={3}>
                  <AntForm.Item label="Scraping details">
                    <Button
                      margin="no small no medium"
                      icon="eye"
                      onClick={() => handleOpenScrapingDebugModal(menu.id)}
                      disabled={isScrapingDetailsButtonDisabled}
                    />
                  </AntForm.Item>
                </Col>
                <Col span={3}>
                  <AntForm.Item label="Scraped document file">
                    {scrapedDocumentFile.fileName ? (
                      <Button
                        style={{ paddingLeft: 0 }}
                        type="link"
                        onClick={() => onDocumentFileNameClick()}
                      >
                        {scrapedDocumentFile.fileName}
                      </Button>
                    ) : (
                      '-'
                    )}
                  </AntForm.Item>
                </Col>
              </Row>
              {menu.scrapingSummary && (
                <>
                  <Row gutter={16}>
                    <Col span={11}>
                      {menu.scrapingSummary?.analysisSlim ? (
                        <ScrapingAnalysis
                          analysis={menu.scrapingSummary?.analysisSlim}
                        />
                      ) : (
                        <AntForm.Item label="Menu analysis">
                          <Typography.Paragraph
                            style={{
                              lineHeight: '1.2em',
                              whiteSpace: 'pre-line',
                            }}
                          >
                            {menu.scrapingSummary?.analysis || EMPTY_VALUE}
                          </Typography.Paragraph>
                        </AntForm.Item>
                      )}
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={11}>
                      <AntForm.Item
                        label="Messages"
                        style={{ maxHeight: '230px', overflowY: 'scroll' }}
                      >
                        {isEmpty(menu.scrapingSummary.messages) ? (
                          EMPTY_VALUE
                        ) : (
                          <List
                            dataSource={menu.scrapingSummary.messages || []}
                            renderItem={message => (
                              <List.Item>{message.text}</List.Item>
                            )}
                          />
                        )}
                      </AntForm.Item>
                    </Col>
                    <Col span={13}>
                      <AntForm.Item
                        label="Exceptions"
                        style={{ maxHeight: '230px', overflowY: 'scroll' }}
                      >
                        {isEmpty(menu.scrapingSummary.exceptions) ? (
                          EMPTY_VALUE
                        ) : (
                          <List
                            dataSource={menu.scrapingSummary.exceptions || []}
                            renderItem={exception => (
                              <List.Item>{exception.text}</List.Item>
                            )}
                          />
                        )}
                      </AntForm.Item>
                    </Col>
                  </Row>
                </>
              )}
              {menu.documentMenuScrapingJob?.scrapingEngine ===
                SCRAPING_ENGINES.PDF_AI && (
                <Row gutter={16}>
                  <Col span={11}>
                    {menu.documentMenuScrapingJob &&
                    menu.documentMenuScrapingJob.jobStatus ===
                      MENU_SCRAPING_JOB_STATUSES.COMPLETED ? (
                      <ScrapingAnalysisAi
                        scrapingJob={menu.documentMenuScrapingJob}
                      />
                    ) : (
                      ''
                    )}
                  </Col>
                </Row>
              )}
            </Collapse.Panel>
          </Collapse>

          {!isEmpty(menuTitlesFields) && (
            <DocumentMenuTitlesAndDishesTables
              menu={menu}
              menuTitles={menuTitlesFields}
              dishes={dishesFields}
              isExpanded={false}
              page={page}
              pageSize={pageSize}
              onShowScrapingDetailsButtonClick={handleOpenScrapingDebugModal}
            />
          )}

          <HiddenInputs
            names={[
              'task.id',
              'task.assignedTo.id',
              'task.assignedTo.name',
              'task.status',
              'task.awaitingReason',
              'task.awaitingReasonComment',
              'task.reopenedDate',
              'task.reopenedBy.id',
              'task.reopenedBy.name',
              'task.reopenReason',
              'task.miscTaskVerifiedBy.id',
              'task.miscTaskVerifiedBy.name',
              'task.miscTaskVerifiedDate',
              'task.miscTaskVerifiedComment',
              'task.miscTaskQCVerifiedBy.id',
              'task.miscTaskQCVerifiedBy.name',
              'task.miscTaskQCVerifiedDate',
              'task.miscTaskQCVerifiedComment',
              'uploadedMenuData',
            ]}
          />
          {map(task.menu.locations, (location, index) => (
            <HiddenInputs
              key={location.id}
              names={[`menu.locations.${index}`]}
            />
          ))}
          <HiddenCheckboxes
            names={['task.isAssigned', 'task.isDone', 'task.isAwaiting']}
          />
        </form>
      </FormProvider>

      <RejectAiMenuScrapingModal
        taskId={task.id}
        menuId={menu.id}
        isModalOpen={isRejectMenuModalOpen}
        setIsModalOpen={setIsRejectMenuModalOpen}
      />

      <MenuAvailabilityCommentModal
        comment={menu.availabilityComment}
        isModalOpen={isMenuAvailabilityCommentModalOpen}
        setIsModalOpen={setIsMenuAvailabilityCommentModalOpen}
      />

      <DocumentMenuScrapingDebugModal
        menu={menu}
        itemHash={selectedItemHash}
        isModalOpen={isScrapingDebugModalOpen}
        closeModal={handleCloseScrapingDebugModal}
      />

      <MarkMiscTaskAsVerifiedModal
        isModalOpen={isMarkMiscTaskAsVerifiedModalOpen}
        isQCVerified={isQCVerified}
        comment={
          isQCVerified ? miscTaskQCVerifiedComment : miscTaskVerifiedComment
        }
        onSubmit={handleMarkMiscTaskAsVerified}
        closeModal={() => setIsMarkMiscTaskAsVerifiedModalOpen(false)}
      />
    </>
  )
}

DocumentDishReviewTaskForm.propTypes = {
  task: PropTypes.object.isRequired,
  isSavingTask: PropTypes.bool.isRequired,
  isResolvingTask: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default DocumentDishReviewTaskForm
